import React from 'react';
import {
  Box,
  IconButton,
  OutlinedInput,
  Typography,
  Chip,
  Paper,
  Divider,
  Stack,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { FilterGroup, FilterCondition } from 'types/userConfig/userConfig';
import { DateTimePicker } from '@mui/x-date-pickers';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { mapFieldToApiField } from 'utils/field-mapping';
import { safeParseJSON } from 'utils/helpers';
import { ChatUserModel } from 'types/chat-users/chat-user';
import dayjs from 'dayjs';
import {
  createDynamicValue,
  isDynamicValue,
  parseRelativeLastDynamic,
  type RelativeLastParts,
} from 'utils/resolve-dynamic-values';

/** ערך תפריט לבחירת "ב-X האחרונים…" — לא נשמר בתצוגה; הערך האמיתי הוא __dynamic__last_N_unit */
const RELATIVE_LAST_MENU_VALUE = '__relative_last_menu__';

const RELATIVE_UNIT_LABEL: Record<RelativeLastParts['unit'], string> = {
  minutes: 'הדקות האחרונות',
  hours: 'השעות האחרונות',
  days: 'הימים האחרונים',
};

/** תווית קריאה לערך יחסי — למשל "ב-15 הדקות האחרונות" */
export function formatRelativeLastLabel(parts: RelativeLastParts): string {
  return `ב-${parts.amount} ${RELATIVE_UNIT_LABEL[parts.unit]}`;
}
import { store } from 'store';

export type ViewFilterOptions = {
  key: keyof ChatUserModel | string; // מאפשר גם שדות שלא קיימים ב-ChatUserModel (כמו IsOpenUpdateTimeFrom)
  label: string | null;
  type: 'input' | 'number' | 'select' | 'dateTime' | 'checkbox';
  options?: Array<{ value: any; label: string | null }>;
  multiple?: boolean;
  operators?: Array<'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startswith' | 'endswith' | 'in' | 'notin' | 'isnull' | 'isnotnull'>;
  category?: 'contact' | 'lastSession' | 'lastMessageObject' | 'lastIncomingMessage' | 'lastOutgoingMessage'; // קטגוריה
  categoryLabel?: string; // שם הקטגוריה בעברית (למשל "איש קשר", "שיחה", "שיחה אחרונה")
};

interface CreateCustomViewFilterGroupsProps {
  options: Array<ViewFilterOptions>;
  filterGroups: FilterGroup[];
  filtersLogic: 'or' | 'and';
  onFilterGroupsChange: (groups: FilterGroup[]) => void;
  onFiltersLogicChange: (logic: 'or' | 'and') => void;
  /** פילטר קבוע מראש (תיקיית שלי / לא משויכות) — תצוגה בלבד */
  readOnly?: boolean;
}

/** מחיקות — רק בריחוף על השורה/הכרטיס; במגע נשאר גלוי לחלקית לנגישות */
const hoverRevealDeleteSx = {
  opacity: 0,
  transition: 'opacity 0.16s ease, color 0.16s ease',
  color: 'text.secondary',
  '&:hover': {
    color: 'error.main',
  },
  '&:focus-visible': { opacity: 1, outline: 'none' },
  '@media (hover: none)': {
    opacity: 0.45,
  },
} as const;

const paperHoverRevealSx = {
  '&:hover .cv-hover-reveal': { opacity: 1 },
  '@media (hover: none)': {
    '& .cv-hover-reveal': { opacity: 0.45 },
  },
} as const;

const operatorLabels: Record<string, string> = {
  eq: 'שווה ל',
  ne: 'לא שווה ל',
  gt: 'גדול מ',
  gte: 'גדול או שווה ל',
  lt: 'קטן מ',
  lte: 'קטן או שווה ל',
  contains: 'מכיל',
  startswith: 'מתחיל ב',
  endswith: 'מסתיים ב',
  in: 'נמצא ב',
  notin: 'לא נמצא ב',
  isnull: 'ריק',
  isnotnull: 'לא ריק',
};

const CreateCustomViewFilterGroups: React.FC<CreateCustomViewFilterGroupsProps> = ({
  options,
  filterGroups,
  filtersLogic,
  onFilterGroupsChange,
  onFiltersLogicChange,
  readOnly = false,
}) => {
  // State לניהול קטגוריות פתוחות/סגורות (אקורדיון)
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set());
  
  // State לניהול איזה Select פתוח (למניעת סגירה אוטומטית)
  const [openSelectId, setOpenSelectId] = React.useState<string | number | null>(null);
  
  // State לזיהוי אם לחיצה אחרונה הייתה על קטגוריה (למניעת סגירה)
  const [lastClickWasCategory, setLastClickWasCategory] = React.useState(false);

  /** הודעות שגיאה בתוך הטופס (טיוטת תנאי / לוגיקה בין תנאים בקבוצה) */
  const [draftInlineErrorByGroup, setDraftInlineErrorByGroup] = React.useState<Record<string, string>>({});
  const [groupLogicErrorByGroup, setGroupLogicErrorByGroup] = React.useState<Record<string, string>>({});

  // פונקציה לפתיחה/סגירה של קטגוריה
  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  /** קבוצה נוספת רק אחרי שיש לפחות תנאי מאושר בקבוצה האחרונה (או שאין עדיין קבוצות — אז מותרת הראשונה) */
  const canAddFilterGroup = React.useMemo(() => {
    if (filterGroups.length === 0) return true;
    const last = filterGroups[filterGroups.length - 1];
    return (last?.conditions?.length ?? 0) > 0;
  }, [filterGroups]);

  const addNewGroup = () => {
    if (!canAddFilterGroup) return;
    const newGroup: FilterGroup = {
      id: Date.now(),
      logic: 'and',
      conditions: [],
    };
    onFilterGroupsChange([...filterGroups, newGroup]);
  };

  const deleteGroup = (groupId: string | number) => {
    onFilterGroupsChange(filterGroups.filter((group) => group.id !== groupId));
  };

  const deleteCondition = (groupId: string | number, conditionId: string | number) => {
    onFilterGroupsChange(
      filterGroups.map((group) =>
        group.id === groupId
          ? { ...group, conditions: group.conditions.filter((cond) => cond.id !== conditionId) }
          : group,
      ),
    );
  };

  const updateGroupLogic = (groupId: string | number, logic: 'or' | 'and') => {
    const group = filterGroups.find((g) => g.id === groupId);
    if (!group) return;
    
    // אם משנים לוגיקה ל"וגם", בדוק אם יש שני תנאים עם אותו שדה
    if (logic === 'and') {
      const singleValueFields = ['lastSession.manager.id', 'label', 'NotificationContactUser', 'LableName'];
      const fieldCounts: Record<string, number> = {};
      const duplicateFields: string[] = [];
      
      // ספור כמה פעמים כל שדה מופיע
      group.conditions.forEach((cond) => {
        if (cond.field && singleValueFields.includes(cond.field)) {
          fieldCounts[cond.field] = (fieldCounts[cond.field] || 0) + 1;
          if (fieldCounts[cond.field] === 2) {
            // מצא את התווית של השדה
            const fieldOption = getFieldOption(cond.field);
            if (fieldOption?.label && !duplicateFields.includes(fieldOption.label)) {
              duplicateFields.push(fieldOption.label);
            }
          }
        }
      });
      
      // אם יש שדה שמופיע יותר מפעם אחת, לא ניתן לשנות ל"וגם"
      const hasDuplicateField = Object.values(fieldCounts).some((count) => count > 1);
      if (hasDuplicateField) {
        const fieldNames = duplicateFields.join(', ');
        setGroupLogicErrorByGroup((prev) => ({
          ...prev,
          [String(groupId)]: `לא ניתן לשנות לוגיקה ל"וגם" כאשר יש מספר תנאים עם אותו שדה (${fieldNames}). שיחה לא יכולה להיות משויכת למספר משתמשים או עם מספר תגיות בו-זמנית.`,
        }));
        return;
      }
    }

    setGroupLogicErrorByGroup((prev) => {
      const next = { ...prev };
      delete next[String(groupId)];
      return next;
    });

    onFilterGroupsChange(
      filterGroups.map((group) => (group.id === groupId ? { ...group, logic } : group)),
    );
  };

  // מחזיר רשימת קטגוריות ייחודיות בסדר שבו הן מופיעות ב-options
  const getCategories = (): Array<{ value: string; label: string }> => {
    const categoriesMap = new Map<string, string>();
    const categoryOrder: string[] = [];
    
    options.forEach((option) => {
      if (option.category && option.categoryLabel) {
        if (!categoriesMap.has(option.category)) {
          categoriesMap.set(option.category, option.categoryLabel);
          categoryOrder.push(option.category);
        }
      }
    });
    
    return categoryOrder.map((category) => ({
      value: category,
      label: categoriesMap.get(category) || category,
    }));
  };

  // מחזיר את כל השדות של קטגוריה מסוימת
  const getFieldsByCategory = (category: string): Array<ViewFilterOptions> => {
    return options.filter((option) => option.category === category);
  };

  const getOperatorsForField = (field: string): Array<'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startswith' | 'endswith' | 'in' | 'notin' | 'isnull' | 'isnotnull'> => {
    // מצא את האופציה לפי ה-key המקורי (לא ה-API field)
    const option = options.find((opt) => String(opt.key) === field);
    if (option?.operators) {
      return option.operators;
    }
    // ברירת מחדל לפי סוג השדה
    if (option?.type === 'select' || option?.type === 'checkbox') {
      return ['eq', 'ne', 'in', 'notin', 'isnull', 'isnotnull'];
    }
    if (option?.type === 'dateTime' || option?.type === 'number') {
      return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'isnull', 'isnotnull'];
    }
    if (option?.type === 'input') {
      return ['eq', 'ne', 'contains', 'startswith', 'endswith', 'isnull', 'isnotnull'];
    }
    return ['eq', 'ne', 'contains', 'isnull', 'isnotnull'];
  };

  const needsValue = (operator: string): boolean => {
    return !['isnull', 'isnotnull'].includes(operator);
  };

  const getFieldOption = (field: string): ViewFilterOptions | undefined => {
    // field הוא כעת ה-key המקורי, לא ה-API field
    return options.find((opt) => String(opt.key) === field);
  };

  /** טיוטת תנאי — מתאשרת לרשימה בלחיצה על "אשר והוסף" */
  const [draftByGroup, setDraftByGroup] = React.useState<Record<string, FilterCondition>>({});

  const defaultDraftCondition = (): FilterCondition => ({ id: 'draft', field: '', operator: 'eq' });

  const getDraft = (groupId: string | number): FilterCondition => {
    const key = String(groupId);
    return draftByGroup[key] ?? defaultDraftCondition();
  };

  const draftSelectKey = (groupId: string | number) => `draft-${groupId}`;

  const updateDraftCondition = (groupId: string | number, updates: Partial<FilterCondition>) => {
    const key = String(groupId);
    setDraftByGroup((prev) => {
      const base = prev[key] ?? defaultDraftCondition();
      return { ...prev, [key]: { ...base, ...updates } };
    });
    setDraftInlineErrorByGroup((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleDraftFieldSelection = (groupId: string | number, newField: string) => {
    const group = filterGroups.find((g) => g.id === groupId);
    if (!group) return;

    const newFieldOption = getFieldOption(newField);
    const singleValueFields = ['lastSession.manager.id', 'label', 'NotificationContactUser', 'LableName'];

    if (singleValueFields.includes(newField)) {
      const existingCondition = group.conditions.find((cond) => cond.field === newField);
      if (existingCondition && (group.logic === 'and' || !group.logic)) {
        const fieldLabel = newFieldOption?.label || newField;
        setDraftInlineErrorByGroup((prev) => ({
          ...prev,
          [String(groupId)]: `לא ניתן לבחור "${fieldLabel}" פעמיים כאשר הלוגיקה היא "וגם". שיחה לא יכולה להיות משויכת למספר משתמשים או עם מספר תגיות בו-זמנית.`,
        }));
        return;
      }
    }

    updateDraftCondition(groupId, {
      field: newField,
      operator: 'eq',
      value: undefined,
    });
  };

  const commitDraft = (groupId: string | number) => {
    const key = String(groupId);
    const draft = getDraft(groupId);
    if (!draft.field?.trim()) {
      setDraftInlineErrorByGroup((prev) => ({ ...prev, [key]: 'בחר שדה לתנאי' }));
      return;
    }
    if (!draft.operator) {
      setDraftInlineErrorByGroup((prev) => ({ ...prev, [key]: 'בחר אופרטור' }));
      return;
    }
    if (needsValue(draft.operator)) {
      const v = draft.value;
      const empty =
        v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
      if (empty) {
        setDraftInlineErrorByGroup((prev) => ({
          ...prev,
          [key]: 'הזן ערך לתנאי או בחר "ריק" / "לא ריק"',
        }));
        return;
      }
    }

    setDraftInlineErrorByGroup((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

    const newCondition: FilterCondition = {
      id: Date.now(),
      field: draft.field,
      operator: draft.operator,
      value: draft.value,
    };

    onFilterGroupsChange(
      filterGroups.map((group) =>
        group.id === groupId ? { ...group, conditions: [...group.conditions, newCondition] } : group,
      ),
    );

    setDraftByGroup((prev) => ({ ...prev, [String(groupId)]: defaultDraftCondition() }));
    setOpenSelectId(null);
  };

  const formatConditionSummary = (condition: FilterCondition): string => {
    const fo = condition.field ? getFieldOption(condition.field) : undefined;
    const fieldLabel = fo?.label || condition.field || '—';
    const categoryLabel = fo?.categoryLabel ? ` (${fo.categoryLabel})` : '';
    const opLabel = operatorLabels[condition.operator] || condition.operator;
    if (condition.operator === 'isnull' || condition.operator === 'isnotnull') {
      return `${fieldLabel}${categoryLabel} · ${opLabel}`;
    }
    // ערך של שדה select מוצג לפי תווית האופציה ולא לפי הערך הפנימי;
    // תגית נשמרת כ-JSON.stringify של אובייקט התגית — מציגים רק את השם.
    const resolveOptionLabel = (raw: unknown): string => {
      const s = String(raw);
      const match = fo?.options?.find((opt) => String(opt.value) === s);
      if (match?.label) return match.label;
      if (s.startsWith('{')) {
        const parsed = safeParseJSON(s);
        if (parsed && typeof parsed === 'object' && 'name' in parsed && parsed.name) {
          return String(parsed.name);
        }
      }
      return s;
    };
    const v = condition.value;
    let val = '';
    const relativeParts = parseRelativeLastDynamic(v);
    if (isDynamicValue(v) && v === createDynamicValue('current_user')) {
      const currentUser = store.getState().user;
      val = `משתמש נוכחי (${currentUser?.FullName || currentUser?.Udid || ''})`;
    } else if (relativeParts) {
      val = formatRelativeLastLabel(relativeParts);
    } else if (isDynamicValue(v)) {
      val =
        v === createDynamicValue('today') ? 'היום' :
        v === createDynamicValue('yesterday') ? 'אתמול' :
        v === createDynamicValue('this_week') ? 'השבוע הנוכחי' :
        v === createDynamicValue('last_week') ? 'השבוע שעבר' :
        v === createDynamicValue('this_month') ? 'החודש הנוכחי' :
        v === createDynamicValue('last_month') ? 'החודש שעבר' :
        String(v);
    } else if (Array.isArray(v)) val = v.map(resolveOptionLabel).join(', ');
    else if (v != null && v !== '') val = resolveOptionLabel(v);
    else val = '…';
    return `${fieldLabel}${categoryLabel} · ${opLabel} · ${val}`;
  };

  return (
    <Box>
      {filterGroups.map((group, groupIndex) => {
        const categories = getCategories();
        const condition = getDraft(group.id);
        const fieldOption = getFieldOption(condition.field);
        const availableOperators = getOperatorsForField(condition.field);

        return (
          <React.Fragment key={group.id}>
            {groupIndex === 1 && filterGroups.length > 1 && !readOnly && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={filtersLogic}
                  onChange={(_, v) => {
                    if (v !== null) onFiltersLogicChange(v as 'or' | 'and');
                  }}
                  aria-label="לוגיקה בין קבוצות"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 1.5,
                      py: 0.25,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      textTransform: 'none',
                    },
                  }}
                >
                  <ToggleButton value="and">וגם</ToggleButton>
                  <ToggleButton value="or">או</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: 1.5,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.divider, 0.9),
              borderRadius: 2,
              bgcolor: 'background.paper',
              ...paperHoverRevealSx,
            }}
          >
            <Box>
            <Box
              sx={{
                mb: groupLogicErrorByGroup[String(group.id)] ? 0.5 : 1.5,
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                minHeight: 40,
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexDirection: 'row',
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.primary"
                sx={{ flex: '1 1 auto', minWidth: 0, textAlign: 'start' }}
              >
                קבוצה {groupIndex + 1}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                {group.conditions.length > 1 && !readOnly && (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={group.logic || 'and'}
                    onChange={(_, v) => {
                      if (v) updateGroupLogic(group.id, v as 'or' | 'and');
                    }}
                    aria-label="לוגיקה בין תנאים בקבוצה"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1.25,
                        py: 0.35,
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        textTransform: 'none',
                      },
                    }}
                  >
                    <ToggleButton value="and">וגם</ToggleButton>
                    <ToggleButton value="or">או</ToggleButton>
                  </ToggleButtonGroup>
                )}
                {!readOnly && (
                  <IconButton
                    className="cv-hover-reveal"
                    size="small"
                    aria-label="מחק קבוצה"
                    onClick={() => deleteGroup(group.id)}
                    disabled={filterGroups.length === 1 && group.conditions.length === 0}
                    sx={hoverRevealDeleteSx}
                  >
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            {groupLogicErrorByGroup[String(group.id)] && (
              <FormHelperText error sx={{ mx: 0, mt: 0, mb: 1.5 }}>
                {groupLogicErrorByGroup[String(group.id)]}
              </FormHelperText>
            )}
            </Box>

            <Stack spacing={1.25} sx={{ width: '100%' }}>
              {group.conditions.length > 0 && (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={0.75}
                  sx={{ width: '100%', gap: 0.75 }}
                >
                  {group.conditions.map((c) => (
                    <Chip
                      key={String(c.id)}
                      label={formatConditionSummary(c)}
                      onDelete={readOnly ? undefined : () => deleteCondition(group.id, c.id)}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}

              {!readOnly && (
              <React.Fragment key={`draft-wrap-${group.id}`}>
              <Box
                    key={`draft-${group.id}`}
                    sx={{
                      borderRadius: 1,
                      py: 0.75,
                      px: 1,
                      mx: -1,
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      '&:hover .cv-hover-reveal': { opacity: 1 },
                      '@media (hover: none)': {
                        '& .cv-hover-reveal': { opacity: 0.45 },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        width: '100%',
                        gap: { xs: 1.25, md: 1.5 },
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'minmax(0, 1fr) 104px minmax(168px, 1.55fr)',
                        },
                        alignItems: 'start',
                      }}
                    >
                      {/* Field */}
                      <Box sx={{ minWidth: 0 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>שדה</InputLabel>
                          <Select
                            value={condition.field || ''}
                            label="שדה"
                            open={openSelectId === draftSelectKey(group.id)}
                            onOpen={() => setOpenSelectId(draftSelectKey(group.id))}
                            onClose={() => {
                              // אם הלחיצה האחרונה הייתה על קטגוריה, אל תסגור
                              if (lastClickWasCategory) {
                                setLastClickWasCategory(false);
                                // אל תסגור את ה-dropdown - נשאיר אותו פתוח
                                return;
                              }
                              setOpenSelectId(null);
                              setLastClickWasCategory(false);
                            }}
                            renderValue={(selected) => {
                              if (!selected || !condition.field) return '';
                              const fieldOption = getFieldOption(condition.field);
                              if (!fieldOption) return condition.field;
                              const categoryLabel = fieldOption.categoryLabel || '';
                              return `${fieldOption.label || condition.field}${categoryLabel ? ` (${categoryLabel})` : ''}`;
                            }}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              // בדוק אם זה קטגוריה או שדה
                              const categoryOption = categories.find((cat) => cat.value === selectedValue);
                              if (categoryOption) {
                                // זה קטגוריה - פתח/סגור אותה
                                toggleCategory(selectedValue);
                                // סמן שהלחיצה האחרונה הייתה על קטגוריה
                                setLastClickWasCategory(true);
                                // אל תסגור את ה-dropdown ואל תעדכן את השדה
                                // נשאיר את ה-dropdown פתוח
                                return;
                              }
                              // זה שדה - עדכן את השדה וסגור את ה-dropdown
                              handleDraftFieldSelection(group.id, selectedValue);
                              setOpenSelectId(null);
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 400,
                                },
                              },
                              onClose: (event, reason) => {
                                // אם הלחיצה האחרונה הייתה על קטגוריה, אל תסגור
                                if (lastClickWasCategory) {
                                  setLastClickWasCategory(false);
                                  // אל תסגור את ה-dropdown - נשאיר אותו פתוח
                                  return;
                                }
                                // סגור רק אם זה backdrop או escape
                                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                                  setOpenSelectId(null);
                                  setLastClickWasCategory(false);
                                }
                              },
                            }}
                          >
                            {categories.map((category) => {
                              const isExpanded = expandedCategories.has(category.value);
                              const categoryFields = getFieldsByCategory(category.value);
                              
                              return (
                                <React.Fragment key={category.value}>
                                  {/* כותרת קטגוריה */}
                                  <MenuItem
                                    value={category.value}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleCategory(category.value);
                                      // אל תסגור את ה-dropdown - נשאיר אותו פתוח
                                    }}
                                    onMouseDown={(e) => {
                                      // מניעת סגירה אוטומטית של ה-dropdown
                                      e.preventDefault();
                                    }}
                                    sx={{
                                      fontWeight: 'bold',
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                      },
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Box>{category.label}</Box>
                                    <Box>
                                      {isExpanded ? (
                                        <ChevronRightIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </Box>
                                  </MenuItem>
                                  {/* שדות תחת הקטגוריה */}
                                  {isExpanded &&
                                    categoryFields.map((fieldOption) => {
                                      // בדוק אם השדה כבר קיים בקבוצה עם לוגיקה "וגם"
                                      const singleValueFields = ['lastSession.manager.id', 'label', 'NotificationContactUser', 'LableName'];
                                      const isDisabled =
                                        singleValueFields.includes(fieldOption.key as string) &&
                                        (group.logic === 'and' || !group.logic) &&
                                        group.conditions.some((cond) => cond.field === fieldOption.key);

                                      return (
                                        <MenuItem
                                          key={fieldOption.key}
                                          value={fieldOption.key}
                                          disabled={isDisabled}
                                          onClick={() => {
                                            handleDraftFieldSelection(group.id, fieldOption.key as string);
                                            setOpenSelectId(null); // סגור את ה-dropdown
                                          }}
                                          sx={{
                                            pl: 6, // הזחה פנימה
                                          }}
                                        >
                                          {fieldOption.label || fieldOption.key}
                                        </MenuItem>
                                      );
                                    })}
                                </React.Fragment>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Operator */}
                      <Box sx={{ minWidth: 0 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>אופרטור</InputLabel>
                          <Select
                            value={condition.operator}
                            label="אופרטור"
                            onChange={(e) => {
                              const newOperator = e.target.value as FilterCondition['operator'];
                              let newValue = condition.value;
                              
                              // אם משנים operator, צריך להתאים את הערך
                              if (needsValue(newOperator)) {
                                const isNewMultipleOperator = newOperator === 'in' || newOperator === 'notin';
                                const isCurrentMultipleOperator = condition.operator === 'in' || condition.operator === 'notin';
                                
                                if (isNewMultipleOperator && !isCurrentMultipleOperator) {
                                  // מעבר מ-eq/ne ל-in/notin - המר string ל-array
                                  newValue = condition.value ? [condition.value] : [];
                                } else if (!isNewMultipleOperator && isCurrentMultipleOperator) {
                                  // מעבר מ-in/notin ל-eq/ne - קח את האלמנט הראשון
                                  newValue = Array.isArray(condition.value) && condition.value.length > 0 
                                    ? condition.value[0] 
                                    : undefined;
                                }
                              } else {
                                // עבור isnull/isnotnull, אין צורך בערך
                                newValue = undefined;
                              }
                              
                              updateDraftCondition(group.id, {
                                operator: newOperator,
                                value: newValue,
                              });
                            }}
                          >
                            {availableOperators.map((op) => (
                              <MenuItem key={op} value={op}>
                                {operatorLabels[op] || op}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Value — עמודה רחבה יותר כדי לאפשר הקלדה נוחה */}
                      <Box
                        sx={{
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                      {needsValue(condition.operator) && condition.field ? (
                        <>
                          {fieldOption?.type === 'select' && (() => {
                            // קבע אם צריך multiple select לפי ה-operator
                            // עבור 'in' ו-'notin' - multiple, אחרת - single
                            const isMultipleOperator = condition.operator === 'in' || condition.operator === 'notin';
                            const shouldBeMultiple = fieldOption.multiple && isMultipleOperator;
                            
                            // קבע את הערך הנוכחי - אם זה array ו-operator הוא eq/ne, קח את האלמנט הראשון
                            let currentValue: any = condition.value;
                            if (Array.isArray(currentValue) && !isMultipleOperator) {
                              currentValue = currentValue[0] || '';
                            } else if (!Array.isArray(currentValue) && isMultipleOperator) {
                              currentValue = currentValue ? [currentValue] : [];
                            } else if (currentValue === null || currentValue === undefined) {
                              currentValue = shouldBeMultiple ? [] : '';
                            }
                            // שמור את הערך המקורי (יכול להיות ערך דינמי)
                            const originalValue = currentValue;

                            // בדיקה אם זה שדה manager - להוסיף אופציה "משתמש נוכחי"
                            // condition.field הוא ה-key המקורי (כמו NotificationContactUser)
                            // אבל צריך לבדוק גם אם זה ממופה ל-managerId ב-API
                            const fieldOptionForCheck = getFieldOption(condition.field);
                            const apiField = fieldOptionForCheck ? mapFieldToApiField(fieldOptionForCheck.key) : condition.field;
                            // «משתמש נוכחי» רלוונטי לשני המנהלים — השיחה והלקוח.
                            // NotificationContactUser נשאר לתצוגות שמורות (ממופה ל-managerId)
                            const isManagerField = condition.field === 'NotificationContactUser' ||
                                                   condition.field === 'lastSession.manager.id' ||
                                                   apiField === 'managerId' ||
                                                   apiField === 'customerManagerUdid';
                            const currentUser = store.getState().user;
                            const currentUserDynamicValue = createDynamicValue('current_user');
                            
                            console.log('Select render - condition.field:', condition.field, 'apiField:', apiField, 'isManagerField:', isManagerField, 'currentUser:', currentUser, 'currentUserDynamicValue:', currentUserDynamicValue, 'fieldOption:', fieldOption);
                            
                            // בדיקה אם הערך הנוכחי הוא ערך דינמי של משתמש נוכחי
                            const isCurrentUserSelected = originalValue === currentUserDynamicValue;

                            return (
                              <FormControl fullWidth size="small">
                                <InputLabel>ערך</InputLabel>
                                <Select
                                  value={originalValue ?? ''}
                                  label="ערך"
                                  displayEmpty
                                  renderValue={(selected) => {
                                    console.log('renderValue called with selected:', selected, 'type:', typeof selected, 'currentUserDynamicValue:', currentUserDynamicValue);
                                    // עבור multiple select, selected הוא array — מציגים תוויות ולא ערכים
                                    if (shouldBeMultiple && Array.isArray(selected)) {
                                      return selected.length > 0
                                        ? selected
                                            .map((sel) => {
                                              const opt = fieldOption.options?.find(
                                                (o) => o.value === sel || String(o.value) === String(sel),
                                              );
                                              return opt?.label ?? String(sel);
                                            })
                                            .join(', ')
                                        : '';
                                    }
                                    // עבור single select
                                    if (selected === currentUserDynamicValue || String(selected) === String(currentUserDynamicValue)) {
                                      return `משתמש נוכחי (${currentUser?.FullName || currentUser?.Udid || ''})`;
                                    }
                                    // מצא את התווית מהאופציות הרגילות
                                    const option = fieldOption.options?.find(opt => opt.value === selected || String(opt.value) === String(selected));
                                    return option ? option.label : selected || '';
                                  }}
                                  onChange={(e) => {
                                    console.log('Select onChange EVENT TRIGGERED!', e);
                                    const value = e.target.value;
                                    console.log('Select onChange - value:', value, 'type:', typeof value, 'isManagerField:', isManagerField, 'currentUserDynamicValue:', currentUserDynamicValue, 'shouldBeMultiple:', shouldBeMultiple);
                                    let finalValue: any;
                                    
                                    if (shouldBeMultiple) {
                                      // עבור multiple select
                                      finalValue = typeof value === 'string' ? value.split(',') : value;
                                    } else {
                                      // עבור single select - תמיד string
                                      finalValue = value || null;
                                    }
                                    
                                    console.log('Calling updateCondition with:', { groupId: group.id, conditionId: condition.id, value: finalValue });
                                    updateDraftCondition(group.id, {
                                      value: finalValue,
                                    });
                                  }}
                                  multiple={shouldBeMultiple}
                                  input={
                                    shouldBeMultiple ? <OutlinedInput label="ערך" /> : undefined
                                  }
                                >
                                  {/* אופציות דינמיות עבור manager */}
                                  {isManagerField && !shouldBeMultiple && currentUser?.Udid && (
                                    <React.Fragment key="current-user-option">
                                      <MenuItem 
                                        key={currentUserDynamicValue}
                                        value={currentUserDynamicValue}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          console.log('MenuItem onClick - currentUserDynamicValue:', currentUserDynamicValue);
                                          // קריאה ישירה ל-onChange
                                          const syntheticEvent = {
                                            target: { value: currentUserDynamicValue },
                                          } as React.ChangeEvent<{ value: unknown }>;
                                          // ננסה לקרוא ל-onChange ישירות
                                          console.log('Calling onChange directly with:', currentUserDynamicValue);
                                          updateDraftCondition(group.id, {
                                            value: currentUserDynamicValue,
                                          });
                                        }}
                                      >
                                        משתמש נוכחי ({currentUser.FullName || currentUser.Udid})
                                      </MenuItem>
                                      <Divider key="divider-after-current-user" />
                                    </React.Fragment>
                                  )}
                                  {fieldOption.options?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            );
                          })()}
                          {(fieldOption?.type === 'input' || fieldOption?.type === 'number') && (
                            <TextField
                              fullWidth
                              size="small"
                              label="ערך"
                              type={fieldOption.type === 'number' ? 'number' : undefined}
                              slotProps={fieldOption.type === 'number' ? { htmlInput: { inputMode: 'numeric' } } : undefined}
                              value={condition.value || ''}
                              onChange={(e) =>
                                updateDraftCondition(group.id, { value: e.target.value })
                              }
                            />
                          )}
                          {fieldOption?.type === 'dateTime' && (() => {
                            // ערך יחסי "ב-X האחרונים" (כמות+יחידה) — כולל ה-presets הישנים 7/30/90 ימים
                            const relativeParts: RelativeLastParts | null = parseRelativeLastDynamic(condition.value);
                            const buildRelativeValue = (amount: number, unit: RelativeLastParts['unit']) =>
                              createDynamicValue(`last_${Math.max(1, Math.floor(amount)) as number}_${unit}` as `last_${number}_${typeof unit}`);
                            return (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 1,
                                width: '100%',
                              }}
                            >
                              <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>ערך דינמי</InputLabel>
                                  <Select
                                    value={
                                      relativeParts
                                        ? RELATIVE_LAST_MENU_VALUE
                                        : isDynamicValue(condition.value)
                                          ? condition.value
                                          : ''
                                    }
                                    label="ערך דינמי"
                                    onChange={(e) => {
                                      if (e.target.value === RELATIVE_LAST_MENU_VALUE) {
                                        // ברירת מחדל: השעה האחרונה; הכמות והיחידה נערכות בשדות שלצד
                                        updateDraftCondition(group.id, {
                                          value: buildRelativeValue(1, 'hours'),
                                        });
                                      } else if (e.target.value) {
                                        updateDraftCondition(group.id, {
                                          value: e.target.value,
                                        });
                                      } else {
                                        // אם בוחרים "בחר תאריך ידני", ננקה את הערך הדינמי
                                        updateDraftCondition(group.id, {
                                          value: null,
                                        });
                                      }
                                    }}
                                  >
                                    <MenuItem value="">בחר תאריך ידני</MenuItem>
                                    <Divider />
                                    <MenuItem value={createDynamicValue('today')}>היום</MenuItem>
                                    <MenuItem value={createDynamicValue('yesterday')}>אתמול</MenuItem>
                                    <MenuItem value={createDynamicValue('this_week')}>השבוע הנוכחי</MenuItem>
                                    <MenuItem value={createDynamicValue('last_week')}>השבוע שעבר</MenuItem>
                                    <MenuItem value={createDynamicValue('this_month')}>החודש הנוכחי</MenuItem>
                                    <MenuItem value={createDynamicValue('last_month')}>החודש שעבר</MenuItem>
                                    <Divider />
                                    <MenuItem value={RELATIVE_LAST_MENU_VALUE}>ב-X האחרונים…</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
                                {relativeParts ? (
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      size="small"
                                      type="number"
                                      label="כמות"
                                      value={relativeParts.amount}
                                      inputProps={{ min: 1, step: 1 }}
                                      onChange={(e) => {
                                        const amount = Number(e.target.value);
                                        if (!Number.isFinite(amount) || amount < 1) return;
                                        updateDraftCondition(group.id, {
                                          value: buildRelativeValue(amount, relativeParts.unit),
                                        });
                                      }}
                                      sx={{ flex: '0 0 40%', minWidth: 0 }}
                                    />
                                    <FormControl size="small" sx={{ flex: '1 1 60%', minWidth: 0 }}>
                                      <InputLabel>יחידה</InputLabel>
                                      <Select
                                        value={relativeParts.unit}
                                        label="יחידה"
                                        onChange={(e) =>
                                          updateDraftCondition(group.id, {
                                            value: buildRelativeValue(
                                              relativeParts.amount,
                                              e.target.value as RelativeLastParts['unit'],
                                            ),
                                          })
                                        }
                                      >
                                        <MenuItem value="minutes">דקות אחרונות</MenuItem>
                                        <MenuItem value="hours">שעות אחרונות</MenuItem>
                                        <MenuItem value="days">ימים אחרונים</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Box>
                                ) : !isDynamicValue(condition.value) ? (
                                  <DateTimePicker
                                    label="ערך"
                                    value={condition.value ? dayjs(condition.value) : null}
                                    onChange={(newValue) =>
                                      updateDraftCondition(group.id, {
                                        value: newValue?.toISOString() || null,
                                      })
                                    }
                                    ampm={false}
                                    sx={{ width: '100%' }}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                      },
                                    }}
                                  />
                                ) : (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="ערך נבחר"
                                    value={
                                      condition.value === createDynamicValue('today') ? 'היום' :
                                      condition.value === createDynamicValue('yesterday') ? 'אתמול' :
                                      condition.value === createDynamicValue('this_week') ? 'השבוע הנוכחי' :
                                      condition.value === createDynamicValue('last_week') ? 'השבוע שעבר' :
                                      condition.value === createDynamicValue('this_month') ? 'החודש הנוכחי' :
                                      condition.value === createDynamicValue('last_month') ? 'החודש שעבר' :
                                      'ערך דינמי'
                                    }
                                    disabled
                                  />
                                )}
                              </Box>
                            </Box>
                            );
                          })()}
                          {!fieldOption && (
                            <TextField
                              fullWidth
                              size="small"
                              label="ערך"
                              value={condition.value || ''}
                              onChange={(e) =>
                                updateDraftCondition(group.id, { value: e.target.value })
                              }
                            />
                          )}
                        </>
                      ) : (
                        <Box sx={{ minHeight: 40 }} aria-hidden />
                      )}
                      </Box>
                    </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      pt: 1,
                      width: '100%',
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => commitDraft(group.id)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      אשר והוסף לרשימה
                    </Button>
                  </Box>
                  </Box>
              {draftInlineErrorByGroup[String(group.id)] && (
                <FormHelperText error sx={{ mx: 0.5, mt: 0.5 }}>
                  {draftInlineErrorByGroup[String(group.id)]}
                </FormHelperText>
              )}
              </React.Fragment>
              )}
            </Stack>
          </Paper>
          </React.Fragment>
        );
      })}

      {!readOnly && (
        <Tooltip
          title="אשר לפחות תנאי אחד בקבוצה הנוכחית לפני הוספת קבוצה נוספת"
          placement="top"
          arrow
          disableHoverListener={canAddFilterGroup}
        >
          <Box component="span" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
            <Button
              type="button"
              variant="text"
              color="primary"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 20 }} />}
              onClick={addNewGroup}
              disabled={!canAddFilterGroup}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 0.5,
                py: 0.25,
                minWidth: 0,
              }}
            >
              הוסף קבוצת פילטרים
            </Button>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default CreateCustomViewFilterGroups;

